import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Spacing16, Spacing4 } from 'autogen/design-tokens/tokens';
import useHiddenItemsObserver from 'hooks/globals/useHiddenItemsObserver';
import { ChanelProductVariants, Option } from '../../shared/types';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';

const squareDimension = '35px';

const List = styled.div<{ showAll: boolean }>`
    display: grid;
    grid-gap: ${Spacing16};
    grid-template-columns: repeat(auto-fill, ${squareDimension});
    height: ${({ showAll }) => (showAll ? 'auto' : squareDimension)};
    overflow: hidden;
    margin: 0;
    padding: ${Spacing4};
    box-sizing: content-box;
    list-style: none;
`;

const Item = styled(LinkAction)<Pick<Option, 'color' | 'isSelected'>>`
    background-color: ${({ color }) => color};
    height: ${squareDimension};

    ${({ isSelected: selected }) =>
        selected &&
        css`
            box-shadow: 0 0 0 2px #fff, 0 0 0 4px #000;
        `}
`;

const ShowAllButton = styled.button<{ showAll: boolean }>`
    position: relative;
    grid-column: -2;
    grid-row: 1;
    border: 0;
    background: 0;

    &:focus {
        outline: 0;
    }

    &::before,
    &:after {
        content: '';
        position: absolute;
        background-color: #000;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
    }

    &:before {
        width: 80%;
        height: 2px;
    }

    &:after {
        height: 80%;
        width: 2px;
    }

    ${({ showAll }) =>
        showAll &&
        css`
            grid-column: auto;
            grid-row: auto;

            &:after {
                display: none;
            }
        `}
`;

export default function ColorPalette(props: ChanelProductVariants) {
    const { options } = props;
    const [showAll, setShowAll] = useState(false);
    const ref = useRef(null);
    const hiddenItemsCount = useHiddenItemsObserver<HTMLDivElement>(ref);
    const showHiddenItemsToggle = hiddenItemsCount > 0;

    function toggleShowAll() {
        setShowAll((prevState) => !prevState);
    }

    return (
        <List showAll={showAll} ref={ref}>
            {options.map((option) => (
                <Item
                    key={option.linkAction.url}
                    color={option.color}
                    action={option.linkAction}
                    title={option.label}
                    isSelected={option.isSelected}
                />
            ))}
            {((showHiddenItemsToggle && !showAll) || showAll) && (
                <ShowAllButton type="button" onClick={toggleShowAll} showAll={showAll} />
            )}
        </List>
    );
}
